import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { readAnswer, resolvePrimaryProgramKey } from '../programs/program-key';

export const USAGE_CAPTURE_KEY = 'usage_capture';
export const usageCaptureModeSchema = z.enum(['counter', 'yes-no']);
export const onboardingUsageCopySchema = z.object({
  weeklyTitle: z.string(),
  weeklySubtitle: z.string(),
  typicalTitle: z.string(),
  typicalSubtitle: z.string(),
  reviewTitle: z.string().optional(),
  reviewBody: z.string().optional(),
  reviewWeekHeading: z.string().optional(),
  reviewTotalLabel: z.string().optional(),
  reviewFreeLabel: z.string().optional(),
  yes: z.string().optional(),
  no: z.string().optional(),
  use: z.string(),
  noUse: z.string(),
  usedDays: z.string(),
  noUseDays: z.string(),
  unanswered: z.string(),
  required: z.string(),
});
export const onboardingUsageProgramSchema = z.object({
  programKey: z.string(),
  priority: z.number(),
  subtypes: z.array(z.string()),
  checkinSchema: z.string(),
  captureMode: usageCaptureModeSchema.nullable(),
  revision: z.string(),
  copy: onboardingUsageCopySchema,
});
export const onboardingUsageConfigSchema = z.object({
  programs: z.array(onboardingUsageProgramSchema),
});
export const onboardingUsageConfigResponseSchema = apiEnvelope(onboardingUsageConfigSchema);
export type OnboardingUsageProgram = z.infer<typeof onboardingUsageProgramSchema>;
export type OnboardingUsageConfig = z.infer<typeof onboardingUsageConfigSchema>;
export type OnboardingUsageCopy = z.infer<typeof onboardingUsageCopySchema>;
export const usageCaptureProvenanceSchema = z.object({
  version: z.literal(1),
  programKey: z.string(),
  checkinSchema: z.string(),
  captureMode: usageCaptureModeSchema,
  revision: z.string(),
  anchor: z.string().date(),
  /** One-based days assumed No at each week's first submission, retained across edits. */
  initialDefaultedNoDays: z
    .object({
      reported: z.array(z.number().int().min(1).max(7)).max(7).optional(),
      typical: z.array(z.number().int().min(1).max(7)).max(7).optional(),
    })
    .optional(),
});
export type UsageCaptureProvenance = z.infer<typeof usageCaptureProvenanceSchema>;

/** Preserve finalization's primary program, then resolve exact keys before ordered subtypes. */
export function selectOnboardingUsageProgram<
  T extends Pick<OnboardingUsageProgram, 'programKey' | 'priority' | 'subtypes'>,
>(programs: readonly T[], answers: Record<string, unknown>): T | undefined {
  const primary = resolvePrimaryProgramKey(answers);
  if (primary === undefined) return undefined;
  return (
    programs.find((program) => program.programKey === primary) ??
    [...programs]
      .sort((a, b) => a.programKey.localeCompare(b.programKey))
      .find((program) => program.subtypes.includes(primary))
  );
}

export function usageProvenance(
  program: UsageCaptureContext,
  anchor: string,
): UsageCaptureProvenance | null {
  return program.captureMode === null
    ? null
    : {
        version: 1,
        programKey: program.programKey,
        checkinSchema: program.checkinSchema,
        captureMode: program.captureMode,
        revision: program.revision,
        anchor,
      };
}
export function hasCompatibleUsageCapture(
  answers: Record<string, unknown>,
  program: UsageCaptureContext,
): boolean {
  const parsed = usageCaptureProvenanceSchema.safeParse(readAnswer(answers, USAGE_CAPTURE_KEY));
  return (
    parsed.success &&
    parsed.data.programKey === program.programKey &&
    parsed.data.checkinSchema === program.checkinSchema &&
    parsed.data.captureMode === program.captureMode &&
    parsed.data.revision === program.revision &&
    parsed.data.anchor === readAnswer(answers, 'week_anchor_date')
  );
}
export function binaryChoiceValue(value: unknown): 0 | 1 | undefined {
  return value === '0' ? 0 : value === '1' ? 1 : undefined;
}
/** Preserve unknown days; callers never default an absent choice to no use. */
export function binaryWeekValues(
  answers: Record<string, unknown>,
  typical = false,
): (0 | 1 | undefined)[] {
  return Array.from({ length: 7 }, (_, i) =>
    binaryChoiceValue(readAnswer(answers, `${typical ? 'typical_' : ''}binary_day_${i + 1}`)),
  );
}

export type UsageCaptureContext = Pick<
  OnboardingUsageProgram,
  'programKey' | 'checkinSchema' | 'captureMode' | 'revision'
>;
export function usageConfigRevision(programKey: string, checkinSchema: string): string {
  return `${programKey}/${checkinSchema}/1`;
}

const onboardingUsageProducerProgramSchema = onboardingUsageProgramSchema
  .extend({ copy: onboardingUsageCopySchema.strict() })
  .strict();
export const onboardingUsageConfigProducerResponseSchema = apiEnvelope(
  z.object({ programs: z.array(onboardingUsageProducerProgramSchema) }).strict(),
);
